import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import RecipeCard from '../components/RecipeCard';
import { RevealItem } from '../components/Reveal';
import SectionBg from '../components/SectionBg';
import { recipes, budgetPresets, occasions } from '../data/recipes';

const gridStagger = {
  initial: 'hidden',
  whileInView: 'show',
  viewport: { once: true, amount: 0.05 },
  variants: { show: { transition: { staggerChildren: 0.06 } } },
};

// "Score IA" simulé : pertinence = note + popularité, bonus si la recette
// utilise bien le budget sans le gaspiller (proximité au plafond).
const aiScore = (r, budget) => {
  const ratingPart = (r.rating / 5) * 50;
  const popPart = Math.min(r.votes / 2500, 1) * 30;
  const fitPart = (r.estimated_cost / budget) * 20; // privilégie les recettes "généreuses"
  return Math.round(ratingPart + popPart + fitPart);
};

const BudgetPage = () => {
  const [params, setParams] = useSearchParams();
  const initial = Number(params.get('max')) || 3000;
  const [budget, setBudget] = useState(initial);
  const [moment, setMoment] = useState(params.get('occasion') || '');

  const results = useMemo(() => {
    return recipes
      .filter((r) => r.estimated_cost <= budget && (!moment || (r.occasions || []).includes(moment)))
      .map((r) => ({ ...r, score: aiScore(r, budget) }))
      .sort((a, b) => b.score - a.score);
  }, [budget, moment]);

  const sync = (b, m) => {
    const next = new URLSearchParams(params);
    next.set('max', String(b));
    if (m) next.set('occasion', m);
    else next.delete('occasion');
    setParams(next, { replace: true });
  };
  const setB = (v) => { setBudget(v); sync(v, moment); };
  const setM = (v) => { setMoment(v); sync(budget, v); };

  return (
    <>
      <Banner>
        <SectionBg tone="white" variant={2} />
        <Inner>
          <AiBadge><i className="ti ti-sparkles" aria-hidden="true" /> Assisté par IA</AiBadge>
          <h1>Trouve les meilleures recettes dans ton budget</h1>
          <p>
            Indique ton budget et le moment : notre IA sélectionne et classe les recettes
            Tartina les plus gourmandes et les mieux notées réalisables.
          </p>

          <Control>
            <Presets>
              {budgetPresets.map((p) => (
                <PresetBtn key={p} $active={budget === p} onClick={() => setB(p)}>
                  ≤ {p.toLocaleString('fr-FR')} F
                </PresetBtn>
              ))}
            </Presets>
            <SliderRow>
              <i className="ti ti-coin" aria-hidden="true" />
              <input type="range" min="500" max="10000" step="500" value={budget}
                onChange={(e) => setB(Number(e.target.value))} />
              <Value>{budget.toLocaleString('fr-FR')} FCFA</Value>
            </SliderRow>
            <MomentLabel><i className="ti ti-calendar-heart" aria-hidden="true" /> Pour quel moment ?</MomentLabel>
            <Moments>
              <MomentChip $active={!moment} onClick={() => setM('')}>Tous les moments</MomentChip>
              {occasions.map((o) => (
                <MomentChip key={o} $active={moment === o} onClick={() => setM(moment === o ? '' : o)}>
                  {o.charAt(0).toUpperCase() + o.slice(1)}
                </MomentChip>
              ))}
            </Moments>
          </Control>
        </Inner>
      </Banner>

      <Inner>
        <ResultHead>
          <h2><b>{results.length}</b> recette{results.length > 1 ? 's' : ''} dans ton budget{moment ? ` pour « ${moment} »` : ''}</h2>
          <Note><i className="ti ti-arrows-sort" aria-hidden="true" /> Classées par pertinence IA</Note>
        </ResultHead>

        {results.length === 0 ? (
          <Empty>
            <i className="ti ti-mood-empty" aria-hidden="true" />
            <p>
              Aucune recette {moment ? `pour « ${moment} » ` : ''}sous {budget.toLocaleString('fr-FR')} FCFA.
              Augmente le budget ou change de moment.
            </p>
          </Empty>
        ) : (
          <Grid as={motion.div} key={`${budget}-${moment}`} {...gridStagger}>
            {results.map((r, i) => (
              <RevealItem key={r.id}>
                <Item>
                  <AiBar>
                    {i === 0 && <BestPick><i className="ti ti-sparkles" aria-hidden="true" /> Choix IA</BestPick>}
                    <Match><i className="ti ti-target-arrow" aria-hidden="true" /> {r.score}% pertinence</Match>
                  </AiBar>
                  <RecipeCard recipe={r} />
                </Item>
              </RevealItem>
            ))}
          </Grid>
        )}
      </Inner>
    </>
  );
};

const Banner = styled.section`
  background: ${({ theme }) => theme.colors.chocolate};
  padding: 44px 0 40px;
  position: relative;
  isolation: isolate;
  h1 { color: #fff; font-size: clamp(1.9rem, 4vw, 2.7rem); margin: 14px 0 10px; }
  p { color: rgba(255,255,255,0.82); max-width: 60ch; margin: 0 0 22px; }
`;
const Inner = styled.div`max-width: 1200px; margin: 0 auto; padding: 0 24px;`;
const AiBadge = styled.span`
  display: inline-flex; align-items: center; gap: 6px; background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.chocolate}; font-weight: 800; font-size: 0.8rem;
  padding: 6px 14px; border-radius: 999px;
`;
const Control = styled.div`
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.18);
  border-radius: 20px; padding: 18px;
`;
const Presets = styled.div`display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 14px;`;
const PresetBtn = styled.button`
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.secondary : 'rgba(255,255,255,0.3)')};
  background: ${({ $active, theme }) => ($active ? theme.colors.secondary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.chocolate : '#fff')};
  font-weight: 800; font-size: 0.9rem; padding: 9px 16px; border-radius: 999px; cursor: pointer;
  transition: all .15s ease;
`;
const SliderRow = styled.div`
  display: flex; align-items: center; gap: 14px;
  i { color: ${({ theme }) => theme.colors.secondary}; font-size: 1.3rem; }
  input { flex: 1; accent-color: ${({ theme }) => theme.colors.secondary}; }
`;
const Value = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading}; font-weight: 700; font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.secondary}; min-width: 130px; text-align: right;
`;
const MomentLabel = styled.div`
  display: flex; align-items: center; gap: 6px; margin: 16px 0 10px;
  font-weight: 800; color: #fff;
  i { color: ${({ theme }) => theme.colors.secondary}; font-size: 1.1rem; }
`;
const Moments = styled.div`display: flex; flex-wrap: wrap; gap: 10px;`;
const MomentChip = styled.button`
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.secondary : 'rgba(255,255,255,0.3)')};
  background: ${({ $active, theme }) => ($active ? theme.colors.secondary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.chocolate : '#fff')};
  font-weight: 800; font-size: 0.88rem; padding: 8px 15px; border-radius: 999px; cursor: pointer;
  text-transform: capitalize; transition: all .15s ease;
`;
const ResultHead = styled.div`
  display: flex; align-items: baseline; justify-content: space-between; gap: 16px;
  margin: 28px 0 18px; flex-wrap: wrap;
  h2 { font-size: 1.5rem; color: ${({ theme }) => theme.colors.chocolate}; font-weight: 600; }
  h2 b { color: ${({ theme }) => theme.colors.primary}; }
`;
const Note = styled.span`
  display: inline-flex; align-items: center; gap: 6px; color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 700; font-size: 0.9rem; i { color: ${({ theme }) => theme.colors.secondaryDark}; }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 26px; padding-bottom: 16px;
`;
const Item = styled.div`display: flex; flex-direction: column; gap: 10px;`;
const AiBar = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap;`;
const Match = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  background: ${({ theme }) => theme.colors.cream}; color: ${({ theme }) => theme.colors.tertiary};
  font-weight: 800; font-size: 0.74rem; padding: 5px 10px; border-radius: 999px;
`;
const BestPick = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  background: ${({ theme }) => theme.colors.primary}; color: #fff;
  font-weight: 800; font-size: 0.74rem; padding: 5px 10px; border-radius: 999px;
`;
const Empty = styled.div`
  text-align: center; padding: 60px 20px; color: ${({ theme }) => theme.colors.textSecondary};
  i { font-size: 2.6rem; color: ${({ theme }) => theme.colors.secondaryDark}; display: block; margin-bottom: 10px; }
`;

export default BudgetPage;
